import React, { useEffect } from 'react';
import { Controller, useFieldArray } from 'react-hook-form';
import moment from 'moment';
import { useSelector } from 'react-redux';
import { FormInputPrice } from '../../../../common/formInputsV2/FormInputPrice';
import { FormInputText } from '../../../../common/formInputsV2/FormInputText';
import { FormInputDate } from '../../../../common/formInputsV2/FormInputDate';
import { FormInputSelect } from '../../../../common/formInputsV2/FormInputSelect';
import FormInputAddressAutoComplete from '../../../../common/formInputsV2/FormInputAddressAutoComplete';
import GuaranteeCommercialCard from '../../GuaranteeCommercialCard';
import { FormInputRadio } from '../../../../common/formInputsV2/FormInputRadio';
import FormReactSelectInputBirthPlace from '../../../../common/formInputsV2/FormReactSelectInputBirthPlace';
import FormInputSelectSearchable from '../../../../common/formInputsV2/FormInputSelectSearchable';
import { commercial_goods } from '../../utils';
import { CAPITAL_LIMITS } from '../../validations/validationRulesProduct95';
import { getRequiredGuaranteesToAdd, getDependentsToRemove } from '../../validations/validationHelpers';

// v2 of InputByType.jsx — forked (not edited in place) because the original
// is shared with Cauzioni's StepGaranzia.jsx.
function NobisCoveragesSection({
  input,
  control,
  watch,
  errors,
  isValid,
  register,
  setValue,
}) {
  const { append, remove } = useFieldArray({
    control,
    name: input.name,
  });

  const buildingType = watch('technical_location_data.tpab_value');
  const coverages = watch(input.name) || [];

  // Only re-sync FABB/RILO when the building type itself actually changes,
  // not on every form edit — otherwise a manual removal of FABB/RILO gets
  // immediately undone by this effect on the next unrelated field change.
  useEffect(() => {
    if (!buildingType) return;

    const propertyValue = watch('property_value') || '';
    const currentCoverages = watch(input.name) || [];

    const getSafeAmount = (code, val) => {
      const limits = CAPITAL_LIMITS[code];
      if (!limits) return val;
      const num = parseFloat(val);
      if (Number.isNaN(num)) return val;
      if (num < limits.min) return limits.min;
      return num;
    };

    if (buildingType === '07') {
      // Fabbricato di Proprietà: Remove RILO, Add FABB
      const riloIndex = currentCoverages.findIndex((f) => f.coverage_code === 'RILO');
      if (riloIndex !== -1) {
        remove(riloIndex);
      }

      const fabbExists = currentCoverages.some((f) => f.coverage_code === 'FABB');
      if (!fabbExists) {
        append({
          coverage_code: 'FABB',
          parameter_collections: [],
          max_amount: getSafeAmount('FABB', propertyValue),
        });
      }
    } else if (buildingType === '08') {
      // Fabbricato in Locazione: Remove FABB, Add RILO
      const fabbIndex = currentCoverages.findIndex((f) => f.coverage_code === 'FABB');
      if (fabbIndex !== -1) {
        remove(fabbIndex);
      }

      const riloExists = currentCoverages.some((f) => f.coverage_code === 'RILO');
      if (!riloExists) {
        append({
          coverage_code: 'RILO',
          parameter_collections: [],
          max_amount: getSafeAmount('RILO', propertyValue),
        });
      }
    }
  }, [buildingType]);

  const handleToggle = (goodId) => {
    const currentCoverages = watch(input.name) || [];
    const currentCodes = currentCoverages.map((c) => c.coverage_code);
    const existingIndex = currentCodes.indexOf(goodId);

    if (existingIndex !== -1) {
      // Unchecking: also cascade-remove anything that required this guarantee
      const codesToRemove = new Set([
        goodId,
        ...getDependentsToRemove(goodId, currentCodes),
      ]);
      currentCoverages
        .map((c, idx) => ({ code: c.coverage_code, idx }))
        .filter((item) => codesToRemove.has(item.code))
        .sort((a, b) => b.idx - a.idx)
        .forEach((item) => remove(item.idx));
    } else {
      // Checking: also auto-select anything this guarantee requires
      const buildingType = watch('technical_location_data.tpab_value');
      const codesToAdd = getRequiredGuaranteesToAdd(goodId, currentCodes, buildingType);
      codesToAdd.forEach((code) => {
        append({ coverage_code: code, parameter_collections: [] });
      });
      append({ coverage_code: goodId, parameter_collections: [] });
    }
  };

  return (
    <div className="mt-3">
      {commercial_goods.map((good) => {
        const coverageIndex = coverages.findIndex((c) => c.coverage_code === good.id);
        const isChecked = coverageIndex !== -1;

        return (
          <GuaranteeCommercialCard
            key={good.id}
            watch={watch}
            name={input.name}
            errors={errors}
            isValid={isValid}
            register={register}
            setValue={setValue}
            good={good}
            index={coverageIndex}
            isChecked={isChecked}
            onToggle={() => handleToggle(good.id)}
          />
        );
      })}
    </div>
  );
}

function DateBirthPlaceSection({
  input, watch, errors, isValid, setValue,
}) {
  const { municipalities, states } = useSelector(
    (store) => store.forms.commercial,
  );
  const formData = watch();

  return (
    <FormReactSelectInputBirthPlace
      states={states}
      options={municipalities}
      label="Luogo di nascita"
      name={input.name}
      values={{
        commune_of_birth_code: formData?.commune_of_birth_code,
        province_of_birth_code: formData?.province_of_birth_code,
        born_abroad: formData.born_abroad,
        country_of_birth_code: formData.country_of_birth_code,
      }}
      onChange={(value) => {
        const touchOptions = { shouldTouch: true, shouldValidate: true, shouldDirty: true };
        setValue(input.name, value.commune_of_birth_code, touchOptions);
        setValue('province_of_birth_code', value.province_of_birth_code, touchOptions);
        setValue('born_abroad', value.born_abroad, touchOptions);
        setValue('country_of_birth_code', value.country_of_birth_code, touchOptions);
      }}
      valid={isValid('commune_of_birth_code')}
      error={errors.commune_of_birth_code?.message}
      valid_country_of_birth_code={
        !!formData.country_of_birth_code && !errors.country_of_birth_code
      }
      error_country_of_birth_code={errors.country_of_birth_code?.message}
    />
  );
}

function InputByTypeV2({
  errors,
  isValid,
  register,
  setValue,
  input,
  control = {},
  watch = () => { },
  formData,
  isModifyState,
}) {
  const getErrorByPath = (path) => path.split('.').reduce((obj, key) => obj?.[key], errors);

  const getErrorMessage = () => {
    const error = getErrorByPath(input.name);

    return error?.message;
  };

  return (
    <>
      {input.type === 'price' && (
        <Controller
          name={input.name}
          control={control}
          render={({ field: { value } }) => (
            <FormInputPrice
              name={input.name}
              value={String(value || '')}
              placeholder={input.placeholder}
              label={input.label}
              error={getErrorMessage()}
              valid={isValid(input.name)}
              onChange={(val) => setValue(input.name, val, {
                shouldValidate: true,
                shouldDirty: true,
                shouldTouch: true,
              })}
            />
          )}
        />
      )}
      {input.type === 'percent' && (
        <Controller
          name={input.name}
          control={control}
          render={({ field: { value } }) => (
            <FormInputPrice
              name={input.name}
              value={String(value || '')}
              placeholder={input.placeholder}
              label={input.label}
              error={getErrorMessage()}
              valid={isValid(input.name)}
              onChange={(val) => setValue(input.name, val, {
                shouldValidate: true,
                shouldDirty: true,
                shouldTouch: true,
              })}
              suffix="%"
              allowDecimals
            />
          )}
        />
      )}
      {input.type === 'text' && (
        <FormInputText
          key={input.name}
          placeholder={input.placeholder}
          label={input.label}
          error={getErrorMessage()}
          valid={isValid(input.name)}
          registration={register(input.name)}
          type={input.inputType || 'text'}
        />
      )}

      {input.type === 'date' && (
        <FormInputDate
          label={input.label}
          registration={register(input.name)}
          minDate={
            input.minDate
              ? moment(input.minDate).format('YYYY-MM-DD')
              : undefined
          }
          maxDate={
            input.maxDate
              ? moment(input.maxDate).format('YYYY-MM-DD')
              : undefined
          }
          error={getErrorMessage()}
          valid={isValid(input.name)}
        />
      )}

      {input.type === 'select' && (
        <>
          {input.searchable ? (
            <FormInputSelectSearchable
              onChange={(selectedValue) => {
                setValue(input.name, selectedValue, {
                  shouldTouch: true,
                  shouldValidate: true,
                  shouldDirty: true,
                });
              }}
              value={formData && formData[input.name] ? formData[input.name] : ''}
              error={getErrorMessage()}
              valid={isValid(input.name)}
              label={input.label}
              placeholder={input.placeholder}
              options={input.options}
            />
          ) : (
            <FormInputSelect
              registration={register(input.name)}
              error={getErrorMessage()}
              valid={isValid(input.name)}
              label={input.label}
              placeholder={input.placeholder}
              options={input.options}
            />
          )}
        </>
      )}

      {input.type === 'address' && (
        <FormInputAddressAutoComplete
          name={input.name}
          setValue={setValue}
          errors={errors}
          isValid={isValid}
          control={control}
          formData={formData}
          isModifyState={isModifyState}
        />
      )}

      {input.type === 'nobis_coverages' && (
        <NobisCoveragesSection
          input={input}
          control={control}
          watch={watch}
          errors={errors}
          isValid={isValid}
          register={register}
          setValue={setValue}
        />
      )}

      {input.type === 'radio' && (
        <FormInputRadio
          label={input.label}
          options={input.options}
          name={input.name}
          registration={register(input.name)}
          error={getErrorMessage()}
        />
      )}

      {input.type === 'date_birth_place' && (
        <DateBirthPlaceSection
          input={input}
          watch={watch}
          errors={errors}
          isValid={isValid}
          setValue={setValue}
        />
      )}
    </>
  );
}

export default InputByTypeV2;
